import { useState, useEffect, useMemo } from 'react';
// import { getAllCourses, saveAnnouncement } from '../../data/data.js';
import {
  getAdminStudents,
  updateAdminStudent,
  updateAdminStudentStatus,
  deleteAdminStudent,
  createAdminStudent,
  getAdminDepartments,
  getCourses,
  getAdminStudentEnrollments,
  assignAdminStudentCourses,
  unassignAdminStudentCourse
} from '../../services/api';
import { useAdmin } from '../../context/AdminContext';
import CertificateModal from '../../components/CertificateModal';

function ManagementStudents() {
  const { searchTerm, setSearchTerm } = useAdmin();
  const [currentFilter, setCurrentFilter] = useState('all');
  const [currentDepartmentFilter, setCurrentDepartmentFilter] = useState('All Department');
  const [studentsData, setStudentsData] = useState([]);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [coursesData, setCoursesData] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [dbDepartments, setDbDepartments] = useState([]);

  // View Student details state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);
  const [viewStudentEnrollments, setViewStudentEnrollments] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  // Assign Course state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignStudent, setAssignStudent] = useState(null);
  const [assignStudentEnrollments, setAssignStudentEnrollments] = useState([]);
  const [selectedCoursesToAssign, setSelectedCoursesToAssign] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [isPaidAssignment, setIsPaidAssignment] = useState(false);
  const [courseToUnassign, setCourseToUnassign] = useState(null);

  // Student form data
  const [studentForm, setStudentForm] = useState({
    id: null,
    name: '',
    email: '',
    phone: '',
    department: '',
    course: '',
    enrollmentDate: '',
    status: 'active',
    progress: 0,
    totalCourses: 0,
    completedCourses: 0,
    certificates: 0,
    lastActive: '',
    avatar: '',
    bio: ''
  });

  const [stats, setStats] = useState({
    totalStudents: 0,
    activeStudents: 0,
    inactiveStudents: 0,
    newStudentsThisMonth: 0,
    totalCoursesEnrolled: 0,
    averageProgress: 0
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  useEffect(() => {
    // Initialize sample data only if needed (for fallback)
    // initializeStudentData();
    fetchStudents();
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Load courses from API
      const coursesRes = await getCourses(true);
      if (coursesRes.success) {
        setCoursesData(coursesRes.data);
        setAvailableCourses(coursesRes.data.map(course => course.title));
      }

      // Load departments from API
      const deptRes = await getAdminDepartments();
      if (deptRes.success) {
        setDbDepartments(deptRes.data);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setCoursesData([]);
      setAvailableCourses([]);
    }
  };

  const fetchStudents = async () => {
    try {
      const result = await getAdminStudents();
      if (result.success) {
        setStudentsData(result.data);
        calculateStatistics(result.data);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  // Filter courses based on department
  useEffect(() => {
    const currentCourses = coursesData || [];

    if (studentForm.department) {
      const dept = studentForm.department.trim();
      const filtered = currentCourses
        .filter(course => (course.department || '').trim() === dept)
        .map(course => course.title);
      setAvailableCourses(filtered);
      
      // Reset course if current selection is not in filtered list
      if (studentForm.course && !filtered.includes(studentForm.course)) {
        setStudentForm(prev => ({ ...prev, course: '' }));
      }
    } else {
      setAvailableCourses(currentCourses.map(course => course.title));
    }
  }, [studentForm.department, coursesData]);

  const initializeStudentData = () => {
    if (!localStorage.getItem('studentsData')) {
      const sampleStudents = [
        {
          id: 1,
          name: 'John Doe',
          email: 'john.doe@example.com',
          phone: '+1 234-567-8901',
          department: 'IT & ECE',
          course: 'Web Development',
          enrollmentDate: '2024-01-15',
          status: 'active',
          progress: 75,
          totalCourses: 5,
          completedCourses: 3,
          certificates: 2,
          lastActive: '2024-12-10',
          avatar: 'https://ui-avatars.com/api/?name=John+Doe&background=00b26f&color=fff',
          bio: 'Passionate learner exploring web technologies'
        },
        {
          id: 2,
          name: 'Jane Smith',
          email: 'jane.smith@example.com',
          phone: '+1 234-567-8902',
          department: 'Civil Engineering',
          course: 'AutoCAD',
          enrollmentDate: '2024-02-20',
          status: 'active',
          progress: 60,
          totalCourses: 3,
          completedCourses: 1,
          certificates: 1,
          lastActive: '2024-12-09',
          avatar: 'https://ui-avatars.com/api/?name=Jane+Smith&background=00b26f&color=fff',
          bio: 'Civil engineering student focused on design'
        },
        {
          id: 3,
          name: 'Mike Johnson',
          email: 'mike.johnson@example.com',
          phone: '+1 234-567-8903',
          department: 'Mechanical Engineering',
          course: 'SolidWorks',
          enrollmentDate: '2024-03-10',
          status: 'inactive',
          progress: 30,
          totalCourses: 4,
          completedCourses: 1,
          certificates: 0,
          lastActive: '2024-11-15',
          avatar: 'https://ui-avatars.com/api/?name=Mike+Johnson&background=00b26f&color=fff',
          bio: 'Mechanical engineering enthusiast'
        },
        {
          id: 4,
          name: 'Sarah Williams',
          email: 'sarah.williams@example.com',
          phone: '+1 234-567-8904',
          department: 'Electrical Vehicle',
          course: 'Electric Vehicle',
          enrollmentDate: '2024-01-25',
          status: 'active',
          progress: 85,
          totalCourses: 6,
          completedCourses: 4,
          certificates: 3,
          lastActive: '2024-12-10',
          avatar: 'https://ui-avatars.com/api/?name=Sarah+Williams&background=00b26f&color=fff',
          bio: 'EV technology specialist in training'
        },
        {
          id: 5,
          name: 'David Brown',
          email: 'david.brown@example.com',
          phone: '+1 234-567-8905',
          department: 'Non-Destructive Testing',
          course: 'NDT',
          enrollmentDate: '2024-04-05',
          status: 'active',
          progress: 45,
          totalCourses: 2,
          completedCourses: 0,
          certificates: 0,
          lastActive: '2024-12-08',
          avatar: 'https://ui-avatars.com/api/?name=David+Brown&background=00b26f&color=fff',
          bio: 'Quality assurance and testing professional'
        }
      ];
      localStorage.setItem('studentsData', JSON.stringify(sampleStudents));
    }
  };

  const loadStudentsData = () => {
    fetchStudents();
  };

  const calculateStatistics = (data) => {
    const students = data || studentsData;
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const activeStudents = students.filter(s => s.status === 'active').length;
    const inactiveStudents = students.filter(s => s.status === 'inactive').length;
    const newStudentsThisMonth = students.filter(s => {
      const enrollmentDate = new Date(s.enrollmentDate);
      return enrollmentDate.getMonth() === currentMonth && enrollmentDate.getFullYear() === currentYear;
    }).length;
    const totalCoursesEnrolled = students.reduce((sum, s) => sum + (s.totalCourses || 0), 0);
    const averageProgress = students.length > 0 ? Math.round(students.reduce((sum, s) => sum + (s.progress || 0), 0) / students.length) : 0;

    setStats({
      totalStudents: students.length,
      activeStudents,
      inactiveStudents,
      newStudentsThisMonth,
      totalCoursesEnrolled,
      averageProgress
    });
  };

  // Get unique departments for filter
  const uniqueDepartments = useMemo(() => {
    const fromStudents = studentsData.map(student => student.department).filter(Boolean);
    const fromCourses = coursesData.map(course => course.department).filter(Boolean);
    const fromDb = dbDepartments;
    return ['All Department', ...new Set([...fromStudents, ...fromCourses, ...fromDb])];
  }, [studentsData, coursesData, dbDepartments]);

  const [localSearch, setLocalSearch] = useState('');

  // Handle search change
  const handleSearchChange = (val) => {
    setLocalSearch(val);
    setSearchTerm(val); 
  };

  // Sync with global searchTerm if it's updated elsewhere
  useEffect(() => {
    setLocalSearch(searchTerm);
  }, [searchTerm]);

  // Direct calculation of display students
  const searchLower = localSearch.toLowerCase().trim();
  const studentsToDisplay = studentsData.filter(student => {
    // Status Filter
    const matchesStatus = currentFilter === 'all' || (student.status || 'active').toLowerCase() === currentFilter.toLowerCase();
    
    // Department Filter
    const matchesDept = currentDepartmentFilter === 'All Department' || student.department === currentDepartmentFilter;
    
    // Search Filter
    if (!searchLower) return matchesStatus && matchesDept;

    const matchesSearch = 
      (student.name || '').toLowerCase().includes(searchLower) ||
      (student.email || '').toLowerCase().includes(searchLower) ||
      (student.department || '').toLowerCase().includes(searchLower) ||
      (student.course || '').toLowerCase().includes(searchLower);

    return matchesStatus && matchesDept && matchesSearch;
  });

  // Replace usage of filteredStudents with memoizedFilteredStudents

  const handleAddStudent = () => {
    setStudentForm({
      id: null,
      name: '',
      email: '',
      phone: '',
      department: '',
      course: '',
      enrollmentDate: new Date().toISOString().split('T')[0],
      status: 'active',
      progress: 0,
      totalCourses: 0,
      completedCourses: 0,
      certificates: 0,
      lastActive: new Date().toISOString().split('T')[0],
      avatar: '',
      bio: ''
    });
    setShowAddStudentModal(true);
  };

  const handleEditStudent = (student) => {
    setStudentForm({ ...student });
    setSelectedStudent(student);
    setShowEditStudentModal(true);
  };

  const handleSaveStudent = async () => {
    try {
      if (studentForm.id) {
        // Update existing student
        const result = await updateAdminStudent(studentForm.id, {
          name: studentForm.name,
          email: studentForm.email,
          status: studentForm.status
        });

        if (result.success) {
          fetchStudents();
          setShowEditStudentModal(false);
          showToast('Student updated successfully', 'success');
        } else {
          showToast(result.message || 'Failed to update student', 'error');
        }
      } else {
        // Add new student
        const result = await createAdminStudent({
          name: studentForm.name,
          email: studentForm.email,
          status: studentForm.status,
          department: studentForm.department,
          course: studentForm.course,
          bio: studentForm.bio
        });

        if (result.success) {
          fetchStudents();
          setShowAddStudentModal(false);
          showToast('Student added and enrolled successfully', 'success');
        } else {
          showToast(result.message || 'Failed to add student', 'error');
        }
      }
    } catch (error) {
      console.error("Error saving student:", error);
      showToast('Error connecting to server', 'error');
    }

    // Clear form
    setStudentForm({
      id: null,
      name: '',
      email: '',
      phone: '',
      department: '',
      course: '',
      enrollmentDate: '',
      status: 'active',
      progress: 0,
      totalCourses: 0,
      completedCourses: 0,
      certificates: 0,
      lastActive: '',
      avatar: '',
      bio: ''
    });
  };

  const handleDeleteStudent = (student) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = async (student) => {
    try {
      const newStatus = student.status === 'active' ? 'inactive' : 'active';
      const result = await updateAdminStudentStatus(student.id, newStatus);
      if (result.success) {
        fetchStudents();
      } else {
        showToast(result.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast('Error connecting to server', 'error');
    }
  };

  const confirmDeleteStudent = async () => {
    try {
      const result = await deleteAdminStudent(studentToDelete.id);
      if (result.success) {
        fetchStudents();
        setShowDeleteModal(false);
        setStudentToDelete(null);
        showToast('Student deleted successfully', 'success');
      } else {
        showToast(result.message || 'Failed to delete student', 'error');
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      showToast('Error connecting to server', 'error');
    }
  };

  // View Details Modal Handlers
  const handleOpenViewModal = async (student) => {
    setViewStudent(student);
    setShowViewModal(true);
    setViewLoading(true);
    try {
      const res = await getAdminStudentEnrollments(student.id);
      if (res.success) {
        setViewStudentEnrollments(res.data);
      } else {
        showToast(res.message || 'Failed to load enrollments', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error fetching student details', 'error');
    } finally {
      setViewLoading(false);
    }
  };

  // Assign Course Modal Handlers
  const handleOpenAssignModal = async (student) => {
    setAssignStudent(student);
    setShowAssignModal(true);
    setAssignLoading(true);
    setSelectedCoursesToAssign([]);
    setIsPaidAssignment(false);
    setCourseToUnassign(null);
    try {
      const res = await getAdminStudentEnrollments(student.id);
      if (res.success) {
        setAssignStudentEnrollments(res.data);
      } else {
        showToast(res.message || 'Failed to load enrollments', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error fetching student details', 'error');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleToggleCourseSelect = (courseId) => {
    setSelectedCoursesToAssign(prev => 
      prev.includes(courseId) ? prev.filter(id => id !== courseId) : [...prev, courseId]
    );
  };

  const handleSaveAssignedCourses = async () => {
    if (selectedCoursesToAssign.length === 0) {
      showToast('Please select at least one course to assign', 'error');
      return;
    }
    setAssignLoading(true);
    try {
      const res = await assignAdminStudentCourses(assignStudent.id, selectedCoursesToAssign, isPaidAssignment);
      if (res.success) {
        showToast(res.message || 'Courses assigned successfully', 'success');
        setShowAssignModal(false);
        fetchStudents(); // Refresh the list
      } else {
        showToast(res.message || 'Failed to assign courses', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error assigning courses', 'error');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleUnassignCourse = async (courseId) => {
    setAssignLoading(true);
    try {
      const res = await unassignAdminStudentCourse(assignStudent.id, courseId);
      if (res.success) {
        showToast(res.message || 'Course unassigned successfully', 'success');
        // Refresh the student's enrollments list in modal
        const enrollmentsRes = await getAdminStudentEnrollments(assignStudent.id);
        if (enrollmentsRes.success) {
          setAssignStudentEnrollments(enrollmentsRes.data);
        }
        fetchStudents(); // Refresh the main students table list
      } else {
        showToast(res.message || 'Failed to unassign course', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Error unassigning course', 'error');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleBulkImport = () => {
    setShowBulkImportModal(true);
  };

  const handleSendAnnouncement = () => {
    setShowAnnouncementModal(true);
  };

  const handleAnnouncementSubmit = (e) => {
    e.preventDefault();
    if (announcementTitle.trim() && announcementMessage.trim()) {
      // Mock announcement save since API is not ready
      console.log('Announcement sent:', { title: announcementTitle, message: announcementMessage });
      setAnnouncementTitle('');
      setAnnouncementMessage('');
      setShowAnnouncementModal(false);
      showToast('Announcement sent successfully to all students!', 'success');
    }
  };

  const handleExportData = () => {
    const csvContent = [
      ['ID', 'Name', 'Email', 'Phone', 'Department', 'Course', 'Enrollment Date', 'Status', 'Progress', 'Total Courses'],
      ...studentsData.map(s => [
        s.id,
        s.name,
        s.email,
        s.phone,
        s.department,
        s.course,
        s.enrollmentDate,
        s.status,
        s.progress + '%',
        s.totalCourses
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `students_data_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 max-md:p-4">
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { icon: 'fa-users', value: stats.totalStudents, label: 'Total Students', gradient: 'from-[#667eea] to-[#764ba2]', trend: '12%' },
          { icon: 'fa-user-check', value: stats.activeStudents, label: 'Active Students', gradient: 'from-[#f093fb] to-[#f5576c]', trend: '8%' },
          { icon: 'fa-user-times', value: stats.inactiveStudents, label: 'Inactive Students', gradient: 'from-[#4facfe] to-[#00f2fe]', trend: '-3%' },
          { icon: 'fa-user-plus', value: stats.newStudentsThisMonth, label: 'New This Month', gradient: 'from-[#43e97b] to-[#38f9d7]', trend: '15%' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-md transition-all relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-1 before:bg-gradient-to-r before:from-[#00b26f] before:to-[#007bff] hover:-translate-y-2 hover:shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl text-white bg-gradient-to-br ${stat.gradient}`}>
                <i className={`fa-solid ${stat.icon}`}></i>
              </div>
              <div className="flex items-center gap-1 text-sm px-2 py-1 rounded-md font-semibold bg-green-50 dark:bg-green-900 text-green-600 dark:text-green-400">
                <i className="fa-solid fa-arrow-up"></i>
                {stat.trend}
              </div>
            </div>
            <div className="text-2xl md:text-3xl font-bold mb-1 text-gray-800 dark:text-gray-100">{stat.value}</div>
            <div className="text-gray-500 dark:text-gray-400 text-sm font-medium">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { icon: 'fa-user-plus', text: 'Add Student', onClick: handleAddStudent },
          { icon: 'fa-file-import', text: 'Bulk Import', onClick: handleBulkImport },
          { icon: 'fa-file-export', text: 'Export Data', onClick: handleExportData },
          { icon: 'fa-envelope', text: 'Send Announcement', onClick: handleSendAnnouncement }
        ].map((action, idx) => (
          <button
            key={idx}
            onClick={action.onClick}
            className="p-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer text-sm md:text-base font-semibold transition-all flex flex-col md:flex-row items-center justify-center md:justify-start gap-2.5 hover:bg-[#00b26f] hover:text-white hover:border-[#00b26f] hover:-translate-y-1 text-gray-700 dark:text-gray-300 shadow-sm"
          >
            <i className={`fa-solid ${action.icon} text-xl`}></i>
            <span className="text-center md:text-left">{action.text}</span>
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 p-4 md:p-8 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-8 gap-4">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <i className="fa-solid fa-users text-[#00b26f]"></i>
            Manage Students
            <span className="text-sm font-medium text-gray-400 ml-2">({studentsToDisplay.length} results)</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
            <div className="relative flex-1 sm:w-64">
              <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search students..."
                value={localSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-100 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium text-sm transition-all focus:border-[#00b26f] outline-none"
              />
            </div>
            <select
              value={currentDepartmentFilter}
              onChange={(e) => setCurrentDepartmentFilter(e.target.value)}
              className="w-full sm:w-56 px-4 py-2.5 border-2 border-gray-100 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium text-sm transition-all focus:border-[#00b26f] outline-none"
            >
              {uniqueDepartments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <div className="flex gap-2 w-full sm:w-auto">
              {['all', 'active', 'inactive'].map(filter => (
                <button
                  key={filter}
                  onClick={() => setCurrentFilter(filter)}
                  className={`flex-1 sm:flex-initial px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${currentFilter === filter ? 'bg-[#00b26f] text-white shadow-lg shadow-[#00b26f]/20' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
              <button
                onClick={() => {
                  setCurrentFilter('all');
                  setCurrentDepartmentFilter('All Department');
                  setSearchTerm('');
                  setLocalSearch('');
                }}
                className="px-4 py-2.5 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-red-500 hover:text-white transition-all text-sm whitespace-nowrap"
                title="Reset All Filters"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700">
                <th className="text-left p-4 font-semibold text-gray-800 dark:text-gray-100">Student</th>
                <th className="text-left p-4 font-semibold text-gray-800 dark:text-gray-100">Contact</th>
                <th className="text-left p-4 font-semibold text-gray-800 dark:text-gray-100">Course</th>
                <th className="text-left p-4 font-semibold text-gray-800 dark:text-gray-100">Status</th>
                <th className="text-left p-4 font-semibold text-gray-800 dark:text-gray-100">Progress</th>
                <th className="text-left p-4 font-semibold text-gray-800 dark:text-gray-100">Actions</th>
              </tr>
            </thead>
            <tbody>
              {studentsToDisplay.map((student) => (
                <tr key={student.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <img src={student.avatar} alt={student.name} className="w-10 h-10 rounded-full border-2 border-gray-100 dark:border-gray-600" />
                        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${student.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                      </div>
                      <div>
                        <div className="font-bold text-gray-800 dark:text-gray-100 group-hover:text-[#00b26f] transition-colors">{student.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">ID: #{student.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{student.email}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{student.phone}</div>
                  </td>
                  <td className="p-4 text-sm text-gray-700 dark:text-gray-300 font-medium">{student.course}</td>
                  <td className="p-4">
                    <button
                      onClick={() => handleToggleStatus(student)}
                      className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold transition-all hover:scale-105 active:scale-95 ${student.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                    >
                      {student.status || 'active'}
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-[80px] bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-[#00b26f] h-full rounded-full transition-all duration-500" style={{ width: `${student.progress}%` }}></div>
                      </div>
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{student.progress}%</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenViewModal(student)}
                        className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all"
                        title="View Details"
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                      <button
                        onClick={() => handleOpenAssignModal(student)}
                        className="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-[#00b26f] dark:text-emerald-400 rounded-lg hover:bg-[#00b26f] hover:text-white transition-all"
                        title="Assign Course"
                      >
                        <i className="fa-solid fa-plus-circle"></i>
                      </button>
                      <button
                        onClick={() => handleEditStudent(student)}
                        className="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all"
                        title="Edit Student"
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteStudent(student)}
                        className="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all"
                        title="Delete Student"
                      >
                        <i className="fa-solid fa-trash-can"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
          {studentsToDisplay.map((student) => (
            <div key={student.id} className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 hover:border-[#00b26f] transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <img src={student.avatar} alt={student.name} className="w-12 h-12 rounded-full border-2 border-white dark:border-gray-700" />
                  <div>
                    <div className="font-bold text-gray-800 dark:text-gray-100">{student.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">ID: #{student.id}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleStatus(student)}
                  className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${student.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                >
                  {student.status || 'active'}
                </button>
              </div>

              <div className="space-y-3 mb-5">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <i className="fa-solid fa-envelope w-4"></i>
                  <span className="truncate">{student.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <i className="fa-solid fa-graduation-cap w-4"></i>
                  <span>{student.course}</span>
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-xs font-bold mb-1 text-gray-500">
                    <span>Progress</span>
                    <span>{student.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-[#00b26f] h-full rounded-full" style={{ width: `${student.progress}%` }}></div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => handleOpenViewModal(student)}
                  className="flex-1 min-w-[70px] py-2 bg-blue-500 text-white rounded-xl text-xs font-bold hover:bg-blue-600 transition-colors flex items-center justify-center gap-1.5"
                >
                  <i className="fa-solid fa-eye text-xs"></i> View
                </button>
                <button
                  onClick={() => handleOpenAssignModal(student)}
                  className="flex-1 min-w-[70px] py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5"
                >
                  <i className="fa-solid fa-plus-circle text-xs"></i> Assign
                </button>
                <button
                  onClick={() => handleEditStudent(student)}
                  className="flex-1 min-w-[70px] py-2 bg-amber-500 text-white rounded-xl text-xs font-bold hover:bg-amber-600 transition-colors flex items-center justify-center gap-1.5"
                >
                  <i className="fa-solid fa-pen-to-square text-xs"></i> Edit
                </button>
                <button
                  onClick={() => handleDeleteStudent(student)}
                  className="px-3 py-2 bg-red-500 text-white rounded-xl text-xs font-bold hover:bg-red-600 transition-colors flex items-center justify-center"
                  title="Delete"
                >
                  <i className="fa-solid fa-trash text-xs"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Student Modal */}
      {showAddStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Add New Student</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Full Name</label>
                <input
                  type="text"
                  value={studentForm.name}
                  onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  placeholder="Enter student name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={studentForm.email}
                  onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                <input
                  type="tel"
                  value={studentForm.phone}
                  onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Department</label>
                <select
                  value={studentForm.department}
                  onChange={(e) => setStudentForm({ ...studentForm, department: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  required
                >
                  <option value="">Select department</option>
                  {uniqueDepartments.filter(d => d !== 'All Department').map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Course</label>
                <select
                  value={studentForm.course}
                  onChange={(e) => setStudentForm({ ...studentForm, course: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  required
                >
                  <option value="">Select course</option>
                  {availableCourses.map((course, index) => (
                    <option key={index} value={course}>{course}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                <select
                  value={studentForm.status}
                  onChange={(e) => setStudentForm({ ...studentForm, status: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Bio</label>
                <textarea
                  value={studentForm.bio}
                  onChange={(e) => setStudentForm({ ...studentForm, bio: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)] resize-none"
                  placeholder="Enter student bio"
                  rows="3"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleSaveStudent}
                className="flex-1 bg-[#00b26f] text-white border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-[#008f5a] hover:-translate-y-0.5"
              >
                Add Student
              </button>
              <button
                onClick={() => setShowAddStudentModal(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-gray-300 dark:hover:bg-gray-500 hover:-translate-y-0.5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Edit Student</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Full Name</label>
                <input
                  type="text"
                  value={studentForm.name}
                  onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  placeholder="Enter student name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={studentForm.email}
                  onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                <input
                  type="tel"
                  value={studentForm.phone}
                  onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                <select
                  value={studentForm.status}
                  onChange={(e) => setStudentForm({ ...studentForm, status: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Bio</label>
                <textarea
                  value={studentForm.bio}
                  onChange={(e) => setStudentForm({ ...studentForm, bio: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)] resize-none"
                  placeholder="Enter student bio"
                  rows="3"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleSaveStudent}
                className="flex-1 bg-[#00b26f] text-white border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-[#008f5a] hover:-translate-y-0.5"
              >
                Update Student
              </button>
              <button
                onClick={() => setShowEditStudentModal(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-gray-300 dark:hover:bg-gray-500 hover:-translate-y-0.5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && studentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Confirm Delete</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete {studentToDelete.name}? This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <button
                onClick={confirmDeleteStudent}
                className="flex-1 bg-red-500 text-white border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-red-600 hover:-translate-y-0.5"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-gray-300 dark:hover:bg-gray-500 hover:-translate-y-0.5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Bulk Import Students</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Upload a CSV file with student data. The file should contain columns: Name, Email, Phone, Department, Course.
            </p>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center mb-6">
              <i className="fa-solid fa-file-csv text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600 dark:text-gray-300 mb-2">Drag and drop CSV file here</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">or</p>
              <button className="mt-2 px-4 py-2 bg-[#00b26f] text-white rounded-lg hover:bg-[#008f5a] transition-colors">
                Browse Files
              </button>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setShowBulkImportModal(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-gray-300 dark:hover:bg-gray-500 hover:-translate-y-0.5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Send Announcement</h2>
            <form onSubmit={handleAnnouncementSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Announcement Title</label>
                <input
                  type="text"
                  value={announcementTitle}
                  onChange={(e) => setAnnouncementTitle(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                  placeholder="Enter announcement title"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Message</label>
                <textarea
                  value={announcementMessage}
                  onChange={(e) => setAnnouncementMessage(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)] resize-none"
                  placeholder="Enter announcement message"
                  rows="4"
                  required
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#00b26f] text-white border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-[#008f5a] hover:-translate-y-0.5"
                >
                  Send Announcement
                </button>
                <button
                  type="button"
                  onClick={() => setShowAnnouncementModal(false)}
                  className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-gray-300 dark:hover:bg-gray-500 hover:-translate-y-0.5"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && viewStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative animate-in zoom-in-95 duration-300">
            <button
              onClick={() => setShowViewModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <i className="fa-solid fa-times text-xl"></i>
            </button>
            
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
              <i className="fa-solid fa-user-graduate text-[#00b26f]"></i>
              Student Profile Details
            </h2>

            {/* Profile Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 pb-6 border-b border-gray-100 dark:border-gray-700">
              <div className="flex flex-col items-center md:items-start text-center md:text-left gap-4">
                <img src={viewStudent.avatar} alt={viewStudent.name} className="w-24 h-24 rounded-full border-4 border-emerald-500/10 shadow-lg object-cover" />
                <div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{viewStudent.name}</h3>
                  <p className="text-sm text-[#00b26f] font-semibold">Student ID: #{viewStudent.id}</p>
                </div>
              </div>
              
              <div className="space-y-3 col-span-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="block text-xs text-gray-400 font-bold uppercase tracking-wider">Email Address</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 break-all">{viewStudent.email}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-gray-400 font-bold uppercase tracking-wider">Phone Number</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{viewStudent.phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-gray-400 font-bold uppercase tracking-wider">Enrollment Date</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{viewStudent.enrollmentDate}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-gray-400 font-bold uppercase tracking-wider">Account Status</span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${viewStudent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {viewStudent.status}
                    </span>
                  </div>
                </div>
                {viewStudent.bio && (
                  <div className="pt-2">
                    <span className="block text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Student Bio</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-700/30 p-3 rounded-xl border border-gray-100 dark:border-gray-700/50">{viewStudent.bio}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Courses Progress list */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <i className="fa-solid fa-book-open text-[#00b26f]"></i>
                Purchased Courses & Progress
              </h3>

              {viewLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
                  <p className="mt-2 text-gray-500 text-sm font-medium">Loading course data...</p>
                </div>
              ) : viewStudentEnrollments.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/20 rounded-2xl border border-gray-100 dark:border-gray-700/50">
                  <i className="fa-solid fa-graduation-cap text-gray-300 text-4xl mb-3"></i>
                  <p className="text-gray-500 font-medium">No courses purchased yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-xl">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                        <th className="text-left p-4 text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Course Name</th>
                        <th className="text-left p-4 text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Enrolled On</th>
                        <th className="text-left p-4 text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Duration</th>
                        <th className="text-left p-4 text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Progress</th>
                        <th className="text-left p-4 text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Certificate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewStudentEnrollments.map((enrollment) => {
                        const isComplete = enrollment.progress >= 99;
                        return (
                          <tr key={enrollment.enrollmentId} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
                            <td className="p-4">
                              <div className="font-bold text-gray-800 dark:text-gray-100">{enrollment.title}</div>
                              <div className="text-xs text-gray-400 font-medium">by {enrollment.instructor}</div>
                            </td>
                            <td className="p-4 text-sm text-gray-600 dark:text-gray-400">{enrollment.enrolledAt}</td>
                            <td className="p-4 text-sm text-gray-600 dark:text-gray-400">{enrollment.duration}</td>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-24 bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden flex-shrink-0">
                                  <div 
                                    className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-green-500' : 'bg-[#00b26f]'}`} 
                                    style={{ width: `${enrollment.progress}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{enrollment.progress}%</span>
                              </div>
                            </td>
                            <td className="p-4">
                              {isComplete && enrollment.certificate_earned ? (
                                <button
                                  type="button"
                                  onClick={() => setSelectedCertificate(enrollment)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-600 dark:text-green-400 rounded-full text-xs font-bold shadow-sm border-none cursor-pointer transition-all hover:scale-105"
                                  title="View Certificate"
                                >
                                  <i className="fa-solid fa-trophy text-yellow-500"></i>
                                  Earned 🏆
                                </button>
                              ) : isComplete ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full text-xs font-bold shadow-sm" title="Exam must be passed to earn certificate">
                                  <i className="fa-solid fa-circle-exclamation text-amber-500"></i>
                                  Exam Pending
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 rounded-full text-xs font-bold">
                                  <i className="fa-solid fa-spinner fa-spin"></i>
                                  In Progress
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex gap-4 mt-8 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setShowViewModal(false)}
                className="w-full sm:w-auto px-6 py-3 bg-[#00b26f] text-white border-none rounded-xl font-bold cursor-pointer transition-all hover:bg-[#008f5a] hover:-translate-y-0.5"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Course Modal */}
      {showAssignModal && assignStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl relative animate-in zoom-in-95 duration-300">
            <button
              onClick={() => setShowAssignModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <i className="fa-solid fa-times text-xl"></i>
            </button>

            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <i className="fa-solid fa-book-medical text-[#00b26f]"></i>
              Assign Courses
            </h2>
            
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Assign one or more courses to <strong>{assignStudent.name}</strong>. Choose whether the courses are assigned for free or by payment.
            </p>

            {assignLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
                <p className="mt-2 text-gray-500 text-sm font-medium">Processing request...</p>
              </div>
            ) : (
              <>
                {/* Active Enrollments */}
                <div className="mb-6">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Already Assigned Courses ({assignStudentEnrollments.length})</h3>
                  {assignStudentEnrollments.length === 0 ? (
                    <p className="text-sm text-gray-500 italic bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg border border-gray-100 dark:border-gray-700/50">No courses assigned yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-1 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-700/50">
                      {assignStudentEnrollments.map(e => (
                        <span key={e.enrollmentId} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-[#00b26f] dark:text-emerald-400 rounded-lg text-xs font-bold border border-emerald-100 dark:border-emerald-900/50">
                          {e.title}
                          <button
                            type="button"
                            onClick={() => setCourseToUnassign({ courseId: e.courseId, title: e.title })}
                            className="text-emerald-600 dark:text-emerald-500 hover:text-red-500 dark:hover:text-red-400 transition-colors flex items-center justify-center"
                            title="Unassign Course"
                          >
                            <i className="fa-solid fa-circle-xmark text-sm"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Assignment Type Options */}
                <div className="mb-6">
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                    Assignment Option
                  </label>
                  <div className="flex bg-gray-100 dark:bg-gray-700/50 p-1 rounded-xl border border-gray-200/50 dark:border-gray-700/50">
                    <button
                      type="button"
                      onClick={() => setIsPaidAssignment(false)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                        !isPaidAssignment
                          ? 'bg-[#00b26f] text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <i className="fa-solid fa-gift"></i>
                      Free
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsPaidAssignment(true)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                        isPaidAssignment
                          ? 'bg-[#00b26f] text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <i className="fa-solid fa-credit-card"></i>
                      Paid (Add to Revenue)
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                    {isPaidAssignment
                      ? "Paid option: Enrolls student and adds course price to the revenue tab (e.g. for offline cash payments)."
                      : "Free option: Enrolls student for free and does not add money to the revenue tab."
                    }
                  </p>
                </div>

                {/* Available Courses list */}
                <div className="mb-6">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Available Courses to Assign</h3>
                  
                  {coursesData.filter(c => !assignStudentEnrollments.map(e => e.courseId).includes(c.id)).length === 0 ? (
                    <p className="text-sm text-gray-500 italic bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg text-center border border-gray-100 dark:border-gray-700/50">All available courses are already assigned to this student.</p>
                  ) : (
                    <div className="space-y-2.5 max-h-[300px] overflow-y-auto p-2 bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50 rounded-xl">
                      {coursesData
                        .filter(c => !assignStudentEnrollments.map(e => e.courseId).includes(c.id))
                        .map((course) => {
                          const isChecked = selectedCoursesToAssign.includes(course.id);
                          return (
                            <label 
                              key={course.id}
                              className={`flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border transition-all cursor-pointer select-none hover:border-emerald-300 dark:hover:border-emerald-800 ${isChecked ? 'border-[#00b26f] ring-2 ring-emerald-500/10' : 'border-gray-100 dark:border-gray-700'}`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleToggleCourseSelect(course.id)}
                                className="mt-1 h-4 w-4 accent-emerald-600 rounded text-white cursor-pointer"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-bold text-gray-800 dark:text-gray-100 line-clamp-1">{course.title}</div>
                                <div className="text-xs text-gray-400 font-medium">Instructor: {course.instructor || 'N/A'} | Price: ₹{course.price}</div>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={handleSaveAssignedCourses}
                    disabled={selectedCoursesToAssign.length === 0}
                    className="flex-1 bg-[#00b26f] text-white border-none py-3 rounded-xl font-bold cursor-pointer transition-all hover:bg-[#008f5a] hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
                  >
                    Assign Selected Courses
                  </button>
                  <button
                    onClick={() => setShowAssignModal(false)}
                    className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-none py-3 rounded-xl font-bold cursor-pointer transition-all hover:bg-gray-300 dark:hover:bg-gray-500 hover:-translate-y-0.5"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Unassign Course Confirmation Modal */}
      {courseToUnassign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-2xl border border-gray-100 dark:border-gray-700 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation text-amber-500"></i>
              Unassign Course
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to unassign <strong>{courseToUnassign.title}</strong>? This will remove the student's access to this course.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  const courseId = courseToUnassign.courseId;
                  setCourseToUnassign(null);
                  handleUnassignCourse(courseId);
                }}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold text-sm transition-all"
              >
                Yes, Unassign
              </button>
              <button
                onClick={() => setCourseToUnassign(null)}
                className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl font-semibold text-sm transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Certificate Preview Modal */}
      <CertificateModal
        isOpen={selectedCertificate !== null}
        onClose={() => setSelectedCertificate(null)}
        studentName={viewStudent?.name || ""}
        courseTitle={selectedCertificate?.title || ""}
        instructor={selectedCertificate?.instructor || ""}
        completionDate={selectedCertificate?.enrolledAt}
        courseId={selectedCertificate?.courseId || selectedCertificate?.course_id || selectedCertificate?.id}
        examScore={selectedCertificate?.examScore || selectedCertificate?.exam_score}
        enrolledAt={selectedCertificate?.enrolledAt || selectedCertificate?.enrolled_at}
        studentPhoto={viewStudent?.image}
      />

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed bottom-6 right-6 z-[9999] animate-[slideIn_0.3s_ease-out]">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border-l-4 min-w-[300px] ${toast.type === 'success'
            ? 'bg-white border-[#00b26f]'
            : 'bg-white border-[#dc3545]'
            }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${toast.type === 'success' ? 'bg-emerald-50 text-[#00b26f]' : 'bg-red-50 text-[#dc3545]'
              }`}>
              <i className={`fa-solid ${toast.type === 'success' ? 'fa-check-circle' : 'fa-circle-exclamation'} text-lg`}></i>
            </div>
            <div className="flex-1">
              <h4 className={`text-sm font-bold ${toast.type === 'success' ? 'text-[#00b26f]' : 'text-[#dc3545]'}`}>
                {toast.type === 'success' ? 'Success' : 'Error'}
              </h4>
              <p className="text-[#333] text-xs font-medium">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast({ ...toast, show: false })}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
      )}

      <style jsx="true">{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default ManagementStudents;
